"use client";
import React, { useEffect, useRef } from 'react';
import { Provider, useDispatch } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import allReducers from '../redux/reducers';
import GlobalCalls from '@/components/@generalComponents/GlobalCalls';
import dynamic from 'next/dynamic';
import { Builder } from '@builder.io/react';
import { AuthContextProvider, UserAuth } from './context/AuthContext';
import Footer from '@/components/layout/Footer';
import TopNav from '@/components/@generalComponents/TopNav';
import { fallbackFooterCategories } from './categories/[slug]/CategoryMessages';
import { bookmarkActions, BOOKMARK_ACTIONS } from '../redux/reducers/bookmark.reducer';
import { followActions, FOLLOW_ACTIONS } from '../redux/reducers/follow.reducer';

const ToasterComponent = dynamic(
  () => import('react-hot-toast').then((mod) => mod.Toaster),
  { ssr: false }
);

const TextBlock = dynamic(() => import('./brand/[brandId]/TextBlock'), {
  ssr: false,
});

// Ensures bookmarks load (and clear) on every auth change, regardless of viewport
const BookmarkInitializer = () => {
  const dispatch = useDispatch();
  const { user, isPro, userLoading } = UserAuth();
  const prevUidRef = useRef(undefined);

  useEffect(() => {
    // Wait until we know the tier before deciding
    if (user && userLoading) return;
    if (user?.uid) {
      // Any signed-in user; reloads on tier change so locked/unlocked refreshes
      dispatch(bookmarkActions.loadBookmarks(user.uid));
    } else if (prevUidRef.current !== undefined) {
      // User was previously logged in and has now logged out — clear bookmarks
      dispatch({
        type: BOOKMARK_ACTIONS.LOAD_BOOKMARKS_SUCCESS,
        payload: { bookmarkIds: [], messages: [] },
      });
    }
    prevUidRef.current = user?.uid;
  }, [user?.uid, isPro, userLoading, dispatch]);

  return null;
};

const FollowInitializer = () => {
  const dispatch = useDispatch();
  const { user, isPro, userLoading } = UserAuth();
  const prevUidRef = useRef(undefined);

  useEffect(() => {
    // Wait until we know the tier before deciding
    if (user && userLoading) return;
    if (user?.uid && isPro) {
      dispatch(followActions.loadFollows(user.uid));
    } else if (user?.uid || prevUidRef.current !== undefined) {
      // Logged out, or not Pro (follows are Pro-only) — clear follows, don't fetch
      dispatch({
        type: FOLLOW_ACTIONS.LOAD_FOLLOWS_SUCCESS,
        payload: { brandIds: [] },
      });
    }
    prevUidRef.current = user?.uid;
  }, [user?.uid, isPro, userLoading, dispatch]);

  return null;
};

const store = configureStore({
  reducer: allReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

Builder.registerComponent(TextBlock, {
  name: 'TextBlock',
  inputs: [
    { name: 'title', type: 'string', defaultValue: 'Default Title' },
    {
      name: 'subContent',
      type: 'richText',
      defaultValue: '<p>This is some <strong>editable</strong> content.</p>',
    },
    { name: 'isFirst', type: 'boolean', defaultValue: false },
  ],
});

export default function ClientRoot({ children }) {
  const navRef = useRef(null);

  // Publish the live top nav height so sticky elements below it (e.g. the left sidebar) line up exactly
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--topnav-h', `${el.offsetHeight}px`);
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(el, { box: 'border-box' });
    return () => observer.disconnect();
  }, []);

  return (
    <AuthContextProvider>
      <Provider store={store}>
        <GlobalCalls />
        <BookmarkInitializer />
        <FollowInitializer />
        <div ref={navRef} className='sticky top-0 z-50 bg-[#00BFFF] px-6 py-3'>
          <TopNav />
        </div>
        {children}

        <Footer showFooter={true} categories={fallbackFooterCategories} />

        <ToasterComponent
          position="bottom-center"
          toastOptions={{
            style: {
              borderRadius: '10px',
              background: '#333',
              color: '#fff',
            },
          }}
        />

      </Provider>
    </AuthContextProvider>
  );
}
